import React, { useState } from 'react';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { auth } from '../../config/firebase';
import Modal from '../ui/Modal/Modal';
import Button from '../ui/Button/Button';
import Input from '../ui/Input/Input';
import './ChangePasswordModal.css';

const ChangePasswordModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Réinitialiser les messages d'erreur lors de la saisie
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateForm = () => {
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('Veuillez remplir tous les champs');
      return false;
    }

    if (formData.newPassword.length < 6) {
      setError('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return false;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('Le nouveau mot de passe doit être différent de l\'ancien');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const user = auth.currentUser;
      
      if (!user || !user.email) {
        throw new Error('Aucun utilisateur connecté');
      }

      // Réauthentifier l'utilisateur avec le mot de passe actuel
      const credential = EmailAuthProvider.credential(
        user.email,
        formData.currentPassword
      );
      
      await reauthenticateWithCredential(user, credential);

      // Mettre à jour le mot de passe
      await updatePassword(user, formData.newPassword);

      setSuccess('Mot de passe modifié avec succès !');
      
      // Réinitialiser le formulaire
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Fermer la modal après 2 secondes
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      
      switch (error.code) {
        case 'auth/wrong-password':
          setError('Mot de passe actuel incorrect');
          break;
        case 'auth/weak-password':
          setError('Le nouveau mot de passe est trop faible');
          break;
        case 'auth/requires-recent-login':
          setError('Veuillez vous reconnecter pour changer votre mot de passe');
          break;
        default:
          setError(error.message || 'Une erreur est survenue lors du changement de mot de passe');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const modalActions = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={isLoading}>
        Annuler
      </Button>
      <Button 
        variant="primary" 
        onClick={handleSubmit} 
        loading={isLoading}
        disabled={isLoading}
        type="submit"
      >
        Modifier le mot de passe
      </Button>
    </>
  );

  return (
    <Modal
      title="Changer le mot de passe"
      actions={modalActions}
      className="cpw-modal-wrapper"
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="cpw-modal-form">
        {error && (
          <div className="cpw-modal-message cpw-modal-message--error">
            {error}
          </div>
        )}
        {success && (
          <div className="cpw-modal-message cpw-modal-message--success">
            {success}
          </div>
        )}

        <Input
          type="password"
          name="currentPassword"
          label="Mot de passe actuel"
          value={formData.currentPassword}
          onChange={handleChange}
          placeholder="Entrez votre mot de passe actuel"
          required
          disabled={isLoading}
        />

        <Input
          type="password"
          name="newPassword"
          label="Nouveau mot de passe"
          value={formData.newPassword}
          onChange={handleChange}
          placeholder="Entrez votre nouveau mot de passe"
          required
          disabled={isLoading}
        />

        <Input
          type="password"
          name="confirmPassword"
          label="Confirmer le nouveau mot de passe"
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="Confirmez votre nouveau mot de passe"
          required
          disabled={isLoading}
        />

        <div className="cpw-modal-info">
          <p className="cpw-modal-info-text">
            Le mot de passe doit contenir au moins 6 caractères.
          </p>
        </div>
      </form>
    </Modal>
  );
};

export default ChangePasswordModal;
